import { useMemo } from 'react'
import { cn } from '@/lib/utils'

function parseDay(iso) {
  if (!iso || typeof iso !== 'string') return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function dayNumber(d) {
  return Math.floor(d.getTime() / 86400000)
}

function formatShort(iso) {
  const d = parseDay(iso)
  if (!d) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function ProjectHubGantt({ schedule, className, onTogglePhase }) {
  const { bars, rangeLabel, empty, ticks } = useMemo(() => {
    const phases = Array.isArray(schedule?.phases) ? schedule.phases : []
    const points = []
    for (const ph of phases) {
      const s = parseDay(ph.start)
      const e = parseDay(ph.end)
      if (s) points.push(s)
      if (e) points.push(e)
    }

    if (!points.length) {
      return { bars: [], rangeLabel: '', ticks: [], empty: true }
    }

    const min = new Date(Math.min(...points.map((p) => p.getTime())))
    const max = new Date(Math.max(...points.map((p) => p.getTime())))
    const minDay = dayNumber(min)
    const maxDay = dayNumber(max)
    const span = Math.max(1, maxDay - minDay)

    const fmt = (d) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

    const rangeLabel = `${fmt(min)} → ${fmt(max)}`

    const gradients = [
      'linear-gradient(90deg, rgba(56,189,248,.95) 0%, rgba(99,102,241,.95) 100%)',
      'linear-gradient(90deg, rgba(244,114,182,.95) 0%, rgba(168,85,247,.95) 100%)',
      'linear-gradient(90deg, rgba(251,191,36,.95) 0%, rgba(249,115,22,.95) 100%)',
      'linear-gradient(90deg, rgba(45,212,191,.95) 0%, rgba(59,130,246,.95) 100%)',
    ]

    const bars = phases
      .map((ph, i) => {
        const s = parseDay(ph.start)
        const e = parseDay(ph.end)
        if (!s || !e) return null
        const sd = dayNumber(s)
        const ed = dayNumber(e)
        const left = ((sd - minDay) / span) * 100
        const width = Math.max(2, ((ed - sd) / span) * 100 || 2)
        return {
          key: ph.id || `ph-${i}`,
          phaseKey: ph.key || `phase-${i}`,
          title: ph.title || `Phase ${i + 1}`,
          start: ph.start || '',
          end: ph.end || '',
          status: ph.status === 'completed' ? 'completed' : 'todo',
          gradient: gradients[i % gradients.length],
          left,
          width,
        }
      })
      .filter(Boolean)

    const tickCount = 5
    const ticks = Array.from({ length: tickCount }, (_, i) => {
      const t = i / (tickCount - 1)
      const day = minDay + Math.round(span * t)
      const d = new Date(day * 86400000)
      return {
        left: t * 100,
        label: fmt(d),
      }
    })

    return { bars, rangeLabel, ticks, empty: false }
  }, [schedule])

  if (empty) {
    return (
      <div
        className={cn(
          'rounded-2xl border border-dashed border-border/80 bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground',
          className
        )}
      >
        Add phases with start and end dates to see a timeline.
      </div>
    )
  }

  return (
    <div className={cn('space-y-3 rounded-2xl border border-border/60 bg-background/30 p-3', className)}>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="font-medium tracking-tight">Gantt overview</span>
        <span className="tabular-nums opacity-80">{rangeLabel}</span>
      </div>
      <div className="grid grid-cols-[100px_1fr] gap-2 text-[11px]">
        <div className="px-1 font-medium text-muted-foreground">Phase</div>
        <div className="relative h-6 rounded-lg border border-border/50 bg-muted/20">
          {ticks.map((t, i) => (
            <div key={`${t.label}-${i}`} className="absolute inset-y-0" style={{ left: `${t.left}%` }}>
              <div className="h-full w-px bg-border/60" />
              <span className="absolute -top-4 -translate-x-1/2 whitespace-nowrap text-[10px] text-muted-foreground">
                {t.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2 pt-2">
        {bars.map((b, idx) => (
          <div key={b.key} className="grid grid-cols-[100px_1fr] items-center gap-2">
            <div className="truncate text-[11px] text-muted-foreground">{b.title}</div>
            <div className="relative h-11 overflow-hidden rounded-lg border border-border/60 bg-gradient-to-b from-muted/30 to-muted/5">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.22),_transparent_52%)] dark:bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.08),_transparent_52%)]" />
              <button
                type="button"
                onClick={() => onTogglePhase?.(b.phaseKey)}
                className="absolute top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-left text-[10px] text-white shadow-sm ring-1 ring-black/20"
                style={{
                  left: `calc(${b.left}% + 2px)`,
                  width: `calc(${b.width}% - 4px)`,
                  minWidth: '116px',
                  background:
                    b.status === 'completed'
                      ? 'linear-gradient(90deg, rgba(16,185,129,.95), rgba(34,197,94,.95))'
                      : b.gradient,
                  opacity: b.status === 'completed' ? 1 : 0.74 + (idx % 4) * 0.05,
                }}
                title={`${b.title} (${formatShort(b.start)} → ${formatShort(b.end)})`}
              >
                <div className="truncate font-medium">{b.title}</div>
                <div className="truncate opacity-90">
                  {formatShort(b.start)} → {formatShort(b.end)} · {b.status === 'completed' ? 'Completed' : 'Mark complete'}
                </div>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
